import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { UserService } from 'app/core/user/user.service';
import { FormsService } from 'app/modules/admin/forms/forms.service';
import { SiteType } from 'app/modules/admin/forms/forms.types';

interface NewSiteForm {
	siteName: FormControl<string>;
	countryId: FormControl<string>;
	erpSiteName: FormControl<string>;
	geotechCalculationMode: FormControl<string>;
}

interface CountryOption {
	id: string;
	countryName: string;
}

@Component({
	selector: 'new-site',
	templateUrl: './new-site.component.html',
})
export class NewSiteComponent implements OnInit {
	site: SiteType;
	newSiteForm: FormGroup<NewSiteForm>;
	countries: CountryOption[] = [];
	saving = false;

	constructor(
		private readonly formService: FormsService,
		private readonly route: Router,
		private readonly userService: UserService,
		private readonly snackBar: MatSnackBar,
	) {}

	ngOnInit(): void {
		// Mesma checagem do botão na listagem. O backend também exige o papel na
		// rota; aqui é só para quem chega digitando a URL.
		const roles = this.userService.getAllAccessGroups();
		if (!Array.isArray(roles) || !roles.includes('site_create')) {
			this.route.navigate(['/apps/sites']);
			return;
		}

		this.newSiteForm = new FormGroup({
			siteName: new FormControl(undefined, Validators.required),
			countryId: new FormControl(undefined, Validators.required),
			erpSiteName: new FormControl(undefined),
			geotechCalculationMode: new FormControl('STANDARD'),
		});

		this.formService.getAllCountries().subscribe({
			next: (countries) => {
				this.countries = countries ?? [];
			},
			error: () => {
				this.snackBar.open('Could not load the country list.', 'OK', {
					duration: 5000,
				});
			},
		});
	}

	sendForm() {
		if (this.newSiteForm.invalid) {
			this.newSiteForm.markAllAsTouched();
			return;
		}

		this.saving = true;
		const data = this.newSiteForm.getRawValue();

		this.formService
			.postEntity('site', {
				...data,
				// Campos legados, iguais em todos os sites e sem uso em cálculo — a
				// coordenada real vem do EPSG do collar. Enviados para manter o
				// padrão do cadastro existente.
				hemisferio: 23654.52,
				meridiano: 85224.98,
				lithologyOptions: [],
			})
			.subscribe({
				next: (site) => {
					this.saving = false;
					this.snackBar.open('Site created.', 'OK', { duration: 3000 });
					// Abre o site recém-criado: ele já nasce utilizável (siteConfig e
					// lithologyOptions criados pelo backend).
					this.route.navigate(['/apps/sites', site.id]);
				},
				error: (error) => {
					this.saving = false;
					const message =
						error?.status === 403
							? 'You do not have permission to create sites.'
							: error?.error?.message || 'Could not create the site.';
					this.snackBar.open(message, 'OK', { duration: 6000 });
				},
			});
	}
}
